import { Routes } from '@angular/router';
import { BaseRoute, SubRoute } from '@core/data-access/models/routes.model';
import { exhibitorFeature } from '@core/data-access/store/exhibitor/exhibitor.reducer';
import { LoginGuardAdmin } from '@core/guards/admin-permission.guard';
import { MaintenancePageGuard } from '@core/guards/maintenance-page.guard';
import { OrderProcessingGuard } from '@core/guards/order-processing.guard';
import { LoginGuardSignedOut } from '@core/guards/user-logout.guard';
import { LoginGuardUser } from '@core/guards/user-permission.guard';
import { newslettersResolver } from '@features/account/features/newsletters/resolvers/newsletters.resolver';
import { provideState } from '@ngrx/store';

export const routes: Routes = [
  {
    path: '',
    title: 'home.title',
    loadComponent: () => import('@pages/home/home.component').then((m) => m.HomeComponent),
    pathMatch: 'full',
    providers: [],
    canActivate: [MaintenancePageGuard, OrderProcessingGuard]
  },
  {
    path: BaseRoute.Translations,
    title: 'translations.title',
    loadComponent: () => import('@pages/translations/translations.component').then((m) => m.TranslationsComponent),
    canActivate: [LoginGuardAdmin, OrderProcessingGuard],
    pathMatch: 'full'
  },
  {
    path: BaseRoute.WidgetAdmin,
    title: 'widget.title',
    loadComponent: () => import('@pages/widget-admin/widget-admin.component').then((m) => m.WidgetAdminComponent),
    canActivate: [LoginGuardAdmin, OrderProcessingGuard],
    pathMatch: 'full'
  },
  {
    path: BaseRoute.DataProtection,
    title: 'data-protection.title',
    loadComponent: () =>
      import('@pages/data-protection-page/data-protection-page.component').then((m) => m.DataProtectionPageComponent),
    pathMatch: 'full'
  },
  {
    path: `${BaseRoute.Translations}/${SubRoute.TranslationDataProtection}`,
    title: 'data-protection.title',
    loadComponent: () =>
      import('@pages/data-protection-page/data-protection-page.component').then((m) => m.DataProtectionPageComponent),
    canActivate: [LoginGuardAdmin],
    pathMatch: 'full'
  },
  {
    path: `${BaseRoute.DataProtection}/:id`,
    title: 'data-protection.title',
    loadComponent: () =>
      import('@pages/data-protection-page/data-protection-page.component').then((m) => m.DataProtectionPageComponent),
    pathMatch: 'full'
  },
  {
    path: `${BaseRoute.Translations}/${SubRoute.TranslationDataProtection}/:id`,
    title: 'data-protection.title',
    loadComponent: () =>
      import('@pages/data-protection-page/data-protection-page.component').then((m) => m.DataProtectionPageComponent),
    canActivate: [LoginGuardAdmin],
    pathMatch: 'full'
  },
  {
    path: BaseRoute.Admin,
    loadChildren: () => import('@features/admin/admin.routes'),
    canActivate: [LoginGuardAdmin]
  },
  {
    path: BaseRoute.Login,
    title: 'login.title',
    loadComponent: () => import('@pages/login-page/login-page.component').then((m) => m.LoginPageComponent),
    data: { type: 'login' },
    pathMatch: 'full',
    canActivate: [LoginGuardSignedOut, MaintenancePageGuard, OrderProcessingGuard]
  },
  {
    path: BaseRoute.Logout,
    title: 'logout.title',
    loadComponent: () => import('@app/logout/logout.component').then((m) => m.LogoutComponent),
    pathMatch: 'full'
  },
  {
    path: BaseRoute.ForgottenPassword,
    title: 'forgotten-password.title',
    loadComponent: () =>
      import('@pages/forgotten-password/forgotten-password.component').then((m) => m.ForgottenPasswordComponent),
    pathMatch: 'full',
    canActivate: [LoginGuardSignedOut, MaintenancePageGuard]
  },
  {
    path: BaseRoute.Maintenance,
    title: 'maintenance.title',
    loadComponent: () =>
      import('@pages/maintenence-page/maintenence-page.component').then((m) => m.MaintenencePageComponent),
    pathMatch: 'full'
  },
  {
    path: `${BaseRoute.PressNews}/:id`,
    title: 'press-news.title',
    loadComponent: () =>
      import('@pages/press-news-detail/press-news-detail.component').then((m) => m.PressNewsDetailComponent),
    pathMatch: 'full'
  },
  {
    path: `${BaseRoute.WebShopDownload}/:id`,
    title: 'download-ticket.title',
    providers: [provideState(exhibitorFeature)],
    loadComponent: () =>
      import('@pages/web-shop-download/web-shop-download.component').then((m) => m.WebShopDownloadComponent),
    pathMatch: 'full'
  },
  {
    path: `${BaseRoute.WebShop}/:id`,
    loadChildren: () => import('@features/web-shop/shell/web-shop.routes')
  },
  {
    path: `${BaseRoute.Series}/:id`,
    title: 'eventseries.title',
    loadComponent: () => import('@pages/event-series/event-series.component').then((m) => m.EventSeriesComponent),
    pathMatch: 'full'
  },
  {
    path: `${BaseRoute.SelfRegistration}/:id`,
    loadChildren: () => import('@features/self-registration/shell/self-registration.routes')
  },
  {
    path: BaseRoute.Profile,
    title: 'profile.title',
    loadComponent: () => import('@pages/profile/profile.component').then((m) => m.ProfileComponent),
    resolve: {
      newsletters: newslettersResolver
    },
    pathMatch: 'full',
    canActivate: [LoginGuardUser, OrderProcessingGuard]
  },
  {
    path: BaseRoute.NewAccount,
    title: 'new-account.title',
    loadComponent: () => import('@pages/new-account/new-account.component').then((m) => m.NewAccountComponent),
    pathMatch: 'full'
  },
  {
    path: `${BaseRoute.ResetPassword}/:hash`,
    title: 'reset-password.title',
    loadComponent: () => import('@pages/reset-password/reset-password.component').then((m) => m.ResetPasswordComponent),
    pathMatch: 'full'
  },
  {
    path: `${BaseRoute.EmailChange}/:hash`,
    title: 'reset-email.title',
    loadComponent: () => import('@pages/reset-email/reset-email.component').then((m) => m.ResetEmailComponent),
    pathMatch: 'full'
  },
  {
    path: `${BaseRoute.ChangePassword}/:hash`,
    title: 'change-password.title',
    loadComponent: () =>
      import('@pages/change-password/change-password.component').then((m) => m.ChangePasswordComponent),
    pathMatch: 'full'
  },
  {
    path: `${BaseRoute.EmailVerify}/:hash`,
    title: 'verify-email.title',
    loadComponent: () => import('@pages/verify-email/verify-email.component').then((m) => m.VerifyEmailComponent),
    pathMatch: 'full'
  },
  {
    path: `${BaseRoute.ConfirmNewsletter}/:hash`,
    title: 'confirm-newsletter.title',
    loadComponent: () =>
      import('@pages/confirm-newsletter/confirm-newsletter.component').then((m) => m.ConfirmNewsletterComponent),
    pathMatch: 'full'
  },

  {
    path: BaseRoute.Widget,
    loadChildren: () => import('@features/widget/shell/widget.routes')
  },
  {
    path: BaseRoute.NotFound,
    title: 'not-found.title',
    loadComponent: () => import('@pages/four-o-four/four-o-four.component').then((m) => m.FourOFourComponent)
  },
  {
    path: '**',
    title: 'not-found.title',
    loadComponent: () => import('@pages/four-o-four/four-o-four.component').then((m) => m.FourOFourComponent)
  }
];
